import { useAuth } from '@/contexts/AuthContext';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Building2, Handshake,
  Settings, LogOut, ShieldCheck, Activity, Globe,
  Menu, X,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import styles from './AppShell.module.css';

interface NavItem {
  to: string;
  labelKey: string;
  icon: ReactNode;
}

interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: 'nav.groups.platform',
    items: [
      { to: '/dashboard', labelKey: 'nav.items.dashboard', icon: <LayoutDashboard size={18} /> },
    ],
  },
  {
    labelKey: 'nav.groups.directory',
    items: [
      { to: '/partners', labelKey: 'nav.items.partners', icon: <Handshake size={18} /> },
      { to: '/tenants',  labelKey: 'nav.items.tenants',  icon: <Building2 size={18} /> },
    ],
  },
  {
    labelKey: 'nav.groups.config',
    items: [
      { to: '/settings', labelKey: 'nav.items.settings', icon: <Settings size={18} /> },
      { to: '/regions',  labelKey: 'nav.items.regions',  icon: <Globe size={18} /> },
    ],
  },
];

const SUPPORTED_LANGS = [
  { code: 'en', nativeName: 'English' },
  { code: 'es', nativeName: 'Español' },
];

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const current = i18n.resolvedLanguage ?? 'en';
  const next = SUPPORTED_LANGS.find(l => l.code !== current) ?? SUPPORTED_LANGS[0];

  return (
    <button
      id="language-switcher"
      title={`Switch to ${next.nativeName}`}
      aria-label={`Switch to ${next.nativeName}`}
      onClick={() => i18n.changeLanguage(next.code)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '3px 8px', borderRadius: 6,
        border: '1px solid var(--color-border)', background: 'transparent',
        cursor: 'pointer', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
        color: 'var(--color-text-3)', transition: 'color 0.15s, border-color 0.15s', minWidth: 34,
      }}
      onMouseEnter={e => { e.currentTarget.style.color = '#818cf8'; e.currentTarget.style.borderColor = '#6366f140'; }}
      onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-text-3)'; e.currentTarget.style.borderColor = 'var(--color-border)'; }}
    >
      {current.toUpperCase()}
    </button>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { platformUser, signOut } = useAuth();
  const { t } = useTranslation();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isActive = (to: string) => currentPath === to || currentPath.startsWith(to + '/');
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className={styles.shell}>
      {/* ── Hamburger (mobile) ────────────── */}
      <button
        className={styles.hamburger}
        onClick={() => setSidebarOpen(o => !o)}
        aria-label="Toggle navigation"
      >
        {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* ── Overlay (mobile) ──────────────── */}
      {sidebarOpen && <div className={styles.overlay} onClick={closeSidebar} />}

      {/* ── Sidebar ───────────────────────── */}
      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
        {/* Logo */}
        <div className={styles.logo}>
          <div className={styles.logoMark}>
            <svg viewBox="0 0 24 24" fill="none">
              <defs>
                <linearGradient id="logoGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#6366F1"/>
                  <stop offset="100%" stopColor="#22D3EE"/>
                </linearGradient>
              </defs>
              <rect width="24" height="24" rx="6" fill="url(#logoGrad)"/>
              <text x="12" y="17" fontFamily="sans-serif" fontSize="12" fontWeight="700"
                    textAnchor="middle" fill="white">e</text>
            </svg>
          </div>
          <span className={styles.logoText}>enska.ai</span>
          <LanguageSwitcher />
        </div>

        {/* Super Admin badge */}
        <div className={styles.tenantBadge}>
          <ShieldCheck size={12} style={{ color: '#6366f1' }} />
          <span className={styles.tenantName} style={{ color: '#a5b4fc' }}>
            {t('auth.platformAdmin')}
          </span>
          <span className={`${styles.planBadge}`} style={{ background: '#6366f120', color: '#818cf8' }}>
            SUPER ADMIN
          </span>
        </div>

        {/* Real-time activity indicator */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '7px 14px', margin: '0 8px 4px',
          borderRadius: 8, background: '#10b98110', border: '1px solid #10b98120',
          fontSize: 11, color: '#34d399',
        }}>
          <Activity size={11} style={{ animation: 'pulse 2s infinite' }} />
          Platform operational
        </div>

        {/* Main navigation */}
        <nav className={styles.nav} aria-label="Main navigation">
          {NAV_GROUPS.map((group) => (
            <div key={group.labelKey} className={styles.navGroup}>
              <span className={styles.navGroupLabel}>{t(group.labelKey)}</span>
              {group.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeSidebar}
                  className={`${styles.navItem} ${isActive(item.to) ? styles.active : ''}`}
                  aria-current={isActive(item.to) ? 'page' : undefined}
                >
                  <span className={styles.navIcon}>{item.icon}</span>
                  <span className={styles.navLabel}>{t(item.labelKey)}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        {/* User menu */}
        {platformUser && (
          <div className={styles.userMenu}>
            <Link
              to="/account"
              onClick={closeSidebar}
              style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, textDecoration: 'none' }}
              title="My account"
            >
              <div className={styles.userAvatar}>
                {platformUser.name?.[0]?.toUpperCase() ?? platformUser.email[0].toUpperCase()}
              </div>
              <div className={styles.userInfo}>
                <p className={styles.userName}>{platformUser.name ?? platformUser.email}</p>
                <p className={styles.userRole}>{platformUser.role}</p>
              </div>
            </Link>
            <button
              id="sign-out-btn"
              className={styles.signOutBtn}
              onClick={signOut}
              title={t('auth.signOut')}
              aria-label={t('auth.signOut')}
            >
              <LogOut size={16} />
            </button>
          </div>
        )}
      </aside>

      {/* ── Main content ──────────────────── */}
      <main className={styles.main}>
        {children}
      </main>
    </div>
  );
}
